import clsx from "clsx";
import { format, addHours } from "date-fns";
import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Icon,
  Loading,
  Modal,
  Spinner,
  Switch,
} from "@smart/itops-sb-design-system-dom";
import { buildDisplayName } from "@smart/itops-utils-basic";
import {
  useApplyFormVersion,
  useLoadFormVersions,
  useUpdateForm,
} from "@smart/manage-gql-client-dom";
import { useAnalyticsEvent } from "@smart/manage-hooks-dom";

import { GqlFormCategory } from "../../../types";

// Move the time back by 2 hours in case the user is in a timezone that is ahead of the saving timezone
const formatDateOffsetHours = -2;
const CREATING_POLL_INTERVAL_MS = 2500;

type FormVersionItem = NonNullable<
  ReturnType<typeof useLoadFormVersions>["result"]
>[number];

const isPublishedVersion = (version: FormVersionItem) =>
  version.versionKind === "published";

const showPublishedMetadata = (version: FormVersionItem) =>
  isPublishedVersion(version) &&
  version.status !== "failed" &&
  version.status !== "creating" &&
  !!version.snapshotHash;

const isRevertDisabled = (version: FormVersionItem) =>
  version.status === "failed" ||
  version.status === "creating" ||
  !version.snapshotHash;

const getVersionKindBadge = (version: FormVersionItem) =>
  isPublishedVersion(version)
    ? {
        label: "Published",
        className:
          "font-semibold text-caption text-green-800 bg-green-20 rounded-small px-[0.6rem]",
      }
    : {
        label: "Auto-save",
        className:
          "font-semibold text-caption bg-neutral-50 rounded-small px-[0.6rem]",
      };

const VersionStatusIcon = ({
  status,
}: {
  status?: FormVersionItem["status"];
}) => {
  switch (status) {
    case "creating":
      return (
        <span
          data-testid="version-history-status-creating"
          className="inline-flex w-[1.6rem] h-[1.6rem] items-center justify-center"
        >
          <Spinner size="regular" color="primary" />
        </span>
      );
    case "failed":
      return (
        <Icon
          name="regularCircleExclamation"
          className="w-[1.6rem] h-[1.6rem] text-red-600"
          dataTestId="version-history-status-failed"
        />
      );
    default:
      return null;
  }
};

type VersionHistoryProps = {
  formUri: string;
  formTitle: string;
  formCategory?: GqlFormCategory;
  confirmationMessage: string;
  creationStatus?: string | null;
  onClose: () => void;
  versionHistoryModal: boolean;
};

export const VersionHistory = ({
  formUri,
  formTitle,
  formCategory,
  confirmationMessage,
  creationStatus,
  onClose,
  versionHistoryModal,
}: VersionHistoryProps) => {
  const [hideAutoVersions, setHideAutoVersions] = useState(false);
  const [revertTo, setRevertTo] = useState<{
    uri: string;
    displayDate: string;
  } | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const { result, loading, startPolling, stopPolling } = useLoadFormVersions({
    formUri,
  });
  const [updateForm] = useUpdateForm();
  const [applyFormVersion] = useApplyFormVersion();
  const { publishEvent } = useAnalyticsEvent();

  const formatDate = (date: string | undefined) =>
    date
      ? format(addHours(new Date(date), formatDateOffsetHours), "dd MMMM yyyy")
      : "";

  const getVersionDisplayDate = (version: FormVersionItem) =>
    formatDate(
      isPublishedVersion(version)
        ? (version.publishedAt ?? version.createdAt)
        : version.createdAt,
    );

  const visibleVersions = useMemo(() => {
    const versions = result ?? [];
    if (!hideAutoVersions) return versions;
    return versions.filter(isPublishedVersion);
  }, [result, hideAutoVersions]);

  const hasAutoVersions = result?.some((v) => !isPublishedVersion(v)) ?? false;
  const hasPublishedVersions = result?.some(isPublishedVersion) ?? false;

  useEffect(() => {
    if (creationStatus !== "reverting") {
      setRevertTo(null);
      onClose();
    }
  }, [creationStatus]);

  useEffect(() => {
    const hasCreating =
      result?.some((version) => version.status === "creating") ?? false;
    if (hasCreating && !isPolling) {
      setIsPolling(true);
      startPolling(CREATING_POLL_INTERVAL_MS);
    } else if (!hasCreating && isPolling) {
      stopPolling();
      setIsPolling(false);
    }
  }, [result, isPolling]);

  const hasVersions = !!result?.length;

  let emptyFilteredMessage: string | null = null;

  if (hideAutoVersions && hasVersions && !visibleVersions.length) {
    emptyFilteredMessage = hasPublishedVersions
      ? "No published versions match this filter."
      : "No published versions are available yet.";
  }

  return (
    <>
      <Modal
        loading={loading}
        header={{ text: "Version History" }}
        open={versionHistoryModal}
        onClose={onClose}
      >
        <div className="flex flex-col gap-medium">
          {hasVersions && hasAutoVersions && (
            <Switch
              text="Hide auto-saves"
              toggled={hideAutoVersions}
              onClick={() => setHideAutoVersions((current) => !current)}
              dataTestId="hide-auto-versions-toggle"
            />
          )}
          <p className="text-caption">
            {hasVersions
              ? "Auto-saved versions are saved once a day. Published versions are saved when the form is published."
              : "No previous versions are available yet."}
          </p>
          {hasVersions && (
            <div
              className={clsx(
                "flex flex-col border border-neutral-100 rounded",
                !emptyFilteredMessage && "max-h-[32rem] overflow-y-auto",
              )}
            >
              {emptyFilteredMessage ? (
                <p className="px-[2.4rem] py-[1.6rem] text-caption text-neutral-700">
                  {emptyFilteredMessage}
                </p>
              ) : (
                visibleVersions.map((version) => {
                  const versionKindBadge = getVersionKindBadge(version);
                  const publishedByName = version.publishedBy
                    ? buildDisplayName(version.publishedBy, "")
                    : "";

                  return (
                    <div
                      className={clsx(
                        "flex justify-between items-center gap-medium px-[2.4rem] py-[1.2rem] border-b border-neutral-100 last:border-b-0",
                      )}
                      key={version.uri}
                      data-testid={`version-history-row-${version.uri}`}
                    >
                      <div className="flex flex-col gap-xSmall min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-small">
                          <span className="text-ui font-semibold">
                            {getVersionDisplayDate(version)}
                          </span>
                          <span className={versionKindBadge.className}>
                            • {versionKindBadge.label}
                          </span>
                          <VersionStatusIcon status={version.status} />
                        </div>
                        {showPublishedMetadata(version) && (
                          <div className="flex flex-col gap-xSmall text-caption text-neutral-500">
                            {publishedByName && (
                              <span>Published by {publishedByName}</span>
                            )}
                            {version.publishNote && (
                              <span>
                                <span className="font-semibold">
                                  Change Note:
                                </span>{" "}
                                {version.publishNote}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        text="Revert"
                        variant="highlight"
                        size="small"
                        disabled={isRevertDisabled(version)}
                        onClick={() => {
                          if (isRevertDisabled(version)) return;
                          setRevertTo({
                            uri: version.uri,
                            displayDate: getVersionDisplayDate(version),
                          });
                        }}
                      />
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </Modal>
      <Modal
        loading={creationStatus === "reverting"}
        dataTestId="revert-confirmation-modal"
        open={!!revertTo}
        onClose={() => setRevertTo(null)}
        header={{
          text: "Revert",
          icon: "regularClockRotateLeft",
          iconBgColor: "bg-cyan-140",
        }}
        footer={{
          buttons: [
            {
              text: "Cancel",
              variant: "secondarySubtle",
              onClick: () => setRevertTo(null),
            },
            {
              text: "Revert",
              variant: "primaryDefault",
              onClick: async () => {
                if (formCategory) {
                  publishEvent({
                    category: "track",
                    name: "IntakeFormVersionRestored",
                    screen: "FormBuilder",
                    entryPoint: "",
                    component: "IntakeFormVersionHistory",
                    jiraKey: "LINK-3019",
                    interactionType: "Active",
                  }).catch(console.error);
                  await updateForm({
                    variables: {
                      uri: formUri,
                      fields: {
                        creationStatus: "reverting",
                        category: formCategory,
                        response: confirmationMessage,
                        title: formTitle,
                      },
                    },
                  });
                  await applyFormVersion({ variables: { uri: revertTo!.uri } });
                }
              },
            },
          ],
        }}
      >
        <div>
          <p>
            You are about to revert this intake form to the version saved on{" "}
            {revertTo?.displayDate}.
          </p>
          <p>
            You will lose any unsaved changes from today. This action can not be
            undone.
          </p>
        </div>
      </Modal>
      {creationStatus === "reverting" && (
        <div
          className="fixed inset-0 z-modal"
          data-testid="reverting-loading-state"
        >
          <Loading />
        </div>
      )}
    </>
  );
};
